import { useEffect } from 'react';

import { setTags, setUser } from '@sentry/react';
import { useQuery } from '@tanstack/react-query';

import { apiClient } from 'api/ApiClient';
import { PROD_KEY_WATCH_REFETCH_INTERVAL, useProdKeyWatchStore } from 'shared/lib/prodKeyWatch/useProdKeyWatchStore';

import { QueryKeys } from './common';

type UseGetProfileParams = {
  enabled?: boolean;
};

export function useGetProfile(params: UseGetProfileParams = { enabled: true }) {
  const { enabled } = params;
  const isWatchingProdKey = useProdKeyWatchStore((state) => state.isWatching);

  const result = useQuery({
    queryKey: [QueryKeys.GetProfile],
    queryFn: () => apiClient.getProfile(),
    enabled,
    staleTime: 1000 * 30,
    // env_types follows the API keys the backend has issued, and those can appear at any time.
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    // Stops as soon as env_types allows a non-sandbox env, so the watch window is only an upper bound.
    refetchInterval: (query) => {
      const canUseProduction = !!query.state.data?.env_types?.some((envType) => envType !== 'sandbox');

      return isWatchingProdKey() && !canUseProduction ? PROD_KEY_WATCH_REFETCH_INTERVAL : false;
    },
  });
  const { isSuccess, data } = result;

  useEffect(() => {
    if (isSuccess && data) {
      const { email, company: { id: company } = {}, is_target_account } = data;

      setTags({ email, company, is_target_account });
      setUser({ email });
    }
  }, [data, isSuccess]);

  return result;
}
